import { DateTime } from "luxon";
import type { Department } from "../contexts/AuthProvider";
import { StaffingIssue } from "./autoStaffing";
import { getPtoStaffingDays, getStaffingDayStart } from "./dateUtils";
import {
	type DepartmentLocation,
	EmploymentType,
	type HospitalData,
	type Nurse,
	type NurseInShift,
	NurseTrainingStatus,
	type PanelShiftData,
	Preferences,
	ShiftType,
	type WeekData,
} from "./models";
import { getDayWeekScheduleIndex, getWeekdayName } from "./utilFunctions";

export const formatDayName = (date: DateTime): string => {
	const dayName = date.toFormat("ccc");
	return dayName.charAt(0).toUpperCase() + dayName.slice(1);
};

export const calculateConsecutiveShifts = (
	weeks: WeekData[],
	nurseId: number,
	currentDayIndex: number,
	weekIndex: number,
	ignoreDate: DateTime | undefined = undefined,
): number => {
	// Function to check if a nurse has a shift on a specific day in a specific week
	const hasShiftOnDayInWeek = (weekData: WeekData | undefined, dayIndex: number): boolean => {
		if (!weekData || !weekData.weeklyShifts) return false;

		for (let shiftIndex = 0; shiftIndex < weekData.weeklyShifts.length; shiftIndex++) {
			const dayData = weekData.weeklyShifts[shiftIndex].days[dayIndex];
			if (dayData?.nurses?.some((n: NurseInShift) => n.nurseId === nurseId && !n.isPendingToDelete)) {
				return true;
			}
		}
		return false;
	};

	// Function to count consecutive shifts in a direction (forwards or backwards)
	const countConsecutive = (week: WeekData | undefined, weekIdx: number, startDay: number, isForward: boolean): number => {
		if (!week) return 0;

		let count = 0;
		let currentWeekIdx = weekIdx;
		let dayIndex = startDay;

		while (true) {
			// Check if we need to move to adjacent week
			if (dayIndex < 0) {
				currentWeekIdx--;
				if (currentWeekIdx < 0) break;
				dayIndex = 6;
				week = weeks[currentWeekIdx];
			} else if (dayIndex > 6) {
				currentWeekIdx++;
				if (currentWeekIdx >= weeks.length) break;
				dayIndex = 0;
				week = weeks[currentWeekIdx];
			}

			if (ignoreDate && ignoreDate === week.weeklyShifts[0].days[dayIndex].date) break;

			// Check if nurse has shift on current day
			if (!week || !hasShiftOnDayInWeek(week, dayIndex)) break;

			count++;
			dayIndex += isForward ? 1 : -1;
		}

		return count;
	};

	// Count backwards from current day (including current day)
	const backwardCount = countConsecutive(weeks[weekIndex], weekIndex, currentDayIndex - 1, false);

	// Count forwards from next day
	const forwardCount = countConsecutive(weeks[weekIndex], weekIndex, currentDayIndex + 1, true);

	return 1 + backwardCount + forwardCount;
};

export const generateShiftData = ({
	nurse,
	weekIndex,
	hospitalData,
	sourceDayIndex,
	targetDayIndex,
	chosenDepartment,
	isSnf,
	locationsMap,
}: {
	nurse: Nurse;
	weekIndex: number;
	hospitalData: HospitalData;
	sourceDayIndex: number;
	targetDayIndex: number;
	chosenDepartment: Department;
	isSnf?: boolean;
	locationsMap?: Map<string, DepartmentLocation>;
}): PanelShiftData[] => {
	// Initialize array with default values for each day of the week
	const shiftData = Array.from<never, PanelShiftData>({ length: 7 }, () => ({
		shiftType: null,
		hasWarning: false,
	}));

	const commonShiftDataProps: Required<Pick<PanelShiftData, "originalDayIndex">> = { originalDayIndex: sourceDayIndex };

	const weekData = hospitalData.weeks[weekIndex];
	if (!weekData || !weekData.weeklyShifts) {
		return shiftData;
	}
	const targetWeekScheduleIndex = getDayWeekScheduleIndex(chosenDepartment, weekData.weeklyShifts[0].days[targetDayIndex].date);
	const relevantPreferences = nurse.employeeSpecificPreferences.filter(
		(pref) =>
			pref.type === Preferences.Avoid &&
			pref.start_date.startOf("day") <= weekData.weeklyShifts[0].days[6].date &&
			pref.end_date >= weekData.weeklyShifts[0].days[0].date,
	);

	for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
		// First check if this is a day off based on preferences
		let foundShift = false;
		const currentShiftDate = weekData.weeklyShifts[0].days[dayIndex].date;
		for (let shiftIndex = 0; shiftIndex < weekData.weeklyShifts.length; shiftIndex++) {
			if (relevantPreferences.length) {
				// Staffing-window overlap: behavior-preserving for full-day PTOs (their day-start-aligned
				// boundaries don't bleed into neighbor days) while also marking exact-times (partial-day)
				// PTO days as "off" (DAT-230).
				const staffingDay = currentShiftDate.startOf("day");
				const preference = relevantPreferences.find(
					(pref) =>
						pref.data?.toLocaleLowerCase() !== "pending_rdo" &&
						getPtoStaffingDays(pref.start_date, pref.end_date, chosenDepartment.dayStart).some((day) => day.hasSame(staffingDay, "day")),
				);
				if (preference) {
					shiftData[dayIndex] = {
						...commonShiftDataProps,
						shiftType: "off",
						tooltip: preference.data,
					};
					foundShift = true;
					break;
				}
			}

			const currentShiftNurses = weekData.weeklyShifts[shiftIndex]?.days[dayIndex]?.nurses || [];
			const isInShift = currentShiftNurses.some((n) => n.nurseId === nurse.id && !n.isPendingToDelete);

			if (isInShift) {
				const shiftType = weekData.weeklyShifts[shiftIndex].type.toLowerCase();
				const consecutiveShifts = calculateConsecutiveShifts(hospitalData.weeks, nurse.id, dayIndex, weekIndex);

				const isRecurringShift = currentShiftNurses.some((nurseShift) => {
					return nurseShift.nurseId === nurse.id && nurseShift.recurringShiftId != null;
				});
				shiftData[dayIndex] = {
					...commonShiftDataProps,
					shiftType: shiftType,
					tooltip: `${consecutiveShifts} consecutive shift${consecutiveShifts > 1 ? "s" : ""}`,
					isRecurringShift: isRecurringShift,
				};
				if (isSnf) {
					const isFromMasterSchedule = currentShiftNurses.some((nurseShift) => {
						nurseShift.recurringShiftId !== undefined;
					});
					shiftData[dayIndex] = {
						...shiftData[dayIndex],
						isFromMasterSchedule,
					};
				}

				foundShift = true;
				break;
			}
		}

		if (foundShift) continue;

		if (dayIndex !== targetDayIndex) {
			shiftData[dayIndex] = {
				...commonShiftDataProps,
				shiftType: null,
				tooltip: "",
			};
			continue;
		}

		const predictedConsecutiveShifts = calculateConsecutiveShifts(
			hospitalData.weeks,
			nurse.id,
			targetDayIndex,
			weekIndex,
			weekData.weeklyShifts[0].days[sourceDayIndex].date,
		);

		// Check nurse's consecutive shift preference
		const consecutiveAllowedShifts = nurse.shiftConstraints?.consecutiveAllowedShifts;

		// Calculate current consecutive shifts before the move
		const currentConsecutiveShifts = calculateConsecutiveShifts(hospitalData.weeks, nurse.id, sourceDayIndex, weekIndex);

		const tooltipMessages: string[] = [];

		const { date: targetShiftDate } = weekData.weeklyShifts[0].days[targetDayIndex];
		const hasWeeklyPreference = nurse.employeeWeeklyPreferences.some((pref) => {
			return (
				(pref.unavailable || pref.type === Preferences.Avoid) &&
				(pref.week_number === null || pref.week_number === targetWeekScheduleIndex) &&
				nurse.shiftType.toLowerCase() === pref.shift.toLowerCase() &&
				getWeekdayName(targetShiftDate.get("day")) === pref.day
			);
		});
		if (hasWeeklyPreference) {
			tooltipMessages.push("Requested to avoid");
		}

		const hasPendingRDO = relevantPreferences.some(
			(pref) =>
				pref.data?.toLocaleLowerCase() === "pending_rdo" &&
				pref.start_date.startOf("day") <= currentShiftDate.startOf("day") &&
				pref.end_date > currentShiftDate.endOf("day"),
		);
		if (hasPendingRDO) {
			tooltipMessages.push("Requested to avoid");
		}

		// Check if move breaks consecutive shifts pattern or exceeds max consecutive shifts
		if (consecutiveAllowedShifts === 3 && currentConsecutiveShifts === 3 && predictedConsecutiveShifts < 3) {
			tooltipMessages.push("Breaks 3 consecutive shifts pattern");
		} else if (consecutiveAllowedShifts === 4 && currentConsecutiveShifts === 4 && predictedConsecutiveShifts < 4) {
			tooltipMessages.push("Breaks 4 consecutive shifts pattern");
		} else if (consecutiveAllowedShifts === 4 && predictedConsecutiveShifts > 4) {
			tooltipMessages.push("Exceeds 4 consecutive shifts preference");
		} else if (consecutiveAllowedShifts === 3 && predictedConsecutiveShifts > 3) {
			tooltipMessages.push("Exceeds 3 consecutive shifts preference");
		}

		const hasWarning = tooltipMessages.length > 0;
		if (!hasWarning) {
			tooltipMessages.push(
				predictedConsecutiveShifts > 0
					? `If moved here: ${predictedConsecutiveShifts} consecutive shift${predictedConsecutiveShifts > 1 ? "s" : ""}`
					: "No consecutive shifts if moved here",
			);
		}

		shiftData[dayIndex] = {
			...commonShiftDataProps,
			shiftType: null,
			tooltip: tooltipMessages.join("; "),
			hasWarning,
		};
	}

	return shiftData;
};

export const createDateRange = (requestDate: DateTime): DateTime[] => {
	const dayOfWeek = requestDate.weekday % 7; // 0 is Sunday, 6 is Saturday
	const startOfWeek = requestDate.minus({ day: dayOfWeek });

	return Array.from({ length: 7 }, (_, i) => startOfWeek.plus({ day: i }));
};

export const getDaysUntilShift = (date: DateTime): number => {
	const today = DateTime.now().startOf("day");
	return date.startOf("day").diff(today, "day").days;
};

export const findDateIndex = (dateRange: DateTime[], targetDate: DateTime): number => {
	return dateRange.findIndex((date) => date.toFormat("yyyy-MM-dd") === targetDate.toFormat("yyyy-MM-dd"));
};

export const isNurseChargeOnDate = (hospitalData: HospitalData, weekIndex: number, shiftIndex: number, dayIndex: number, nurseId: number): boolean => {
	if (!hospitalData?.weeks?.[weekIndex]?.weeklyShifts?.[shiftIndex]?.days?.[dayIndex]?.nurses) {
		return false;
	}

	return hospitalData.weeks[weekIndex].weeklyShifts[shiftIndex].days[dayIndex].nurses.some((nurse) => nurse.nurseId === nurseId && nurse.charge) || false;
};

export const calcualteStaffingData = (hospitalData: HospitalData, weekIndex: number) => {
	const staffingData = {
		dayShift: Array(7)
			.fill(null)
			.map(() => ({ nurses: 0, staffNo: 0 })),
		nightShift: Array(7)
			.fill(null)
			.map(() => ({ nurses: 0, staffNo: 0 })),
	};

	// Populate staffing data if available
	if (weekIndex >= 0 && hospitalData.weeks[weekIndex].weeklyShifts) {
		const weekData = hospitalData.weeks[weekIndex];

		// Process day shift data (index 0)
		if (weekData.weeklyShifts[0]?.days) {
			weekData.weeklyShifts[0].days.forEach((day, i) => {
				staffingData.dayShift[i] = {
					nurses: (day.nurses || []).filter((n) => !n.isPendingToDelete).length,
					staffNo: day.staffNo || 0,
				};
			});
		}

		// Process night shift data (index 1)
		if (weekData.weeklyShifts[1]?.days) {
			weekData.weeklyShifts[1].days.forEach((day, i) => {
				staffingData.nightShift[i] = {
					nurses: (day.nurses || []).filter((n) => !n.isPendingToDelete).length,
					staffNo: day.staffNo || 0,
				};
			});
		}
	}

	return staffingData;
};
